import {useState} from "react";
import {App as AntdApp, Popconfirm, Table, Tag, Tooltip} from "antd";
import className from "classnames/bind";
import {Button, FontAwesomeIcon} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useAddCompetitorMutation,
	useAnalyzeCompetitorMutation,
	useAnalyzeOwnSiteMutation,
	useDeleteCompetitorMutation,
	useGetCompetitorsQuery,
	useUpdateCompetitorMutation,
} from "~/reduxs/api/competitorApiSlice";
import CompetitorAddModal from "./CompetitorAddModal";
import {GRADE_COLORS} from "./gradeMeta";
import style from "../style/Competitor.module.scss";

const cn = className.bind(style);

/**
 * Tab "Đối thủ" — danh sách đối thủ của dự án (trần theo gói max_competitors):
 * thêm/sửa/xoá + chấm điểm SEO on-page từng đối thủ và site của chính mình.
 * Chấm điểm dùng lại bản trong 24h (miễn phí); quá 24h hoặc Chấm lại → tốn 1 lượt
 * "kiểm tra URL" của gói.
 */
function CompetitorList({projectId}) {

	const {notification} = AntdApp.useApp();
	const {data, isFetching} = useGetCompetitorsQuery(projectId);

	const [addCompetitor, {isLoading: adding}] = useAddCompetitorMutation();
	const [updateCompetitor, {isLoading: updating}] = useUpdateCompetitorMutation();
	const [deleteCompetitor] = useDeleteCompetitorMutation();
	const [analyzeCompetitor] = useAnalyzeCompetitorMutation();
	const [analyzeOwn, {isLoading: analyzingOwn}] = useAnalyzeOwnSiteMutation();

	const [modalOpen, setModalOpen] = useState(false);
	const [editing, setEditing] = useState(null);   // đối thủ đang sửa (null = thêm mới)
	const [analyzingId, setAnalyzingId] = useState(null);

	const competitors = data?.competitors || [];
	const own = data?.own || null;
	const max = data?.max || 0;

	const notifyAnalyzeResult = (res, label) => {
		if (res?.cached) {
			notification.info({
				message: 'Dùng bản chấm gần nhất',
				description: `${label} đã được chấm trong 24h — không tính thêm lượt kiểm tra URL.`,
			});
		} else {
			notification.success({message: 'Đã chấm điểm', description: `${label}: ${res?.report?.score ?? '—'}/100`});
		}
	};

	const onAnalyze = async (row, refresh = false) => {
		try {
			setAnalyzingId(row.id);
			const res = await analyzeCompetitor({projectId, id: row.id, refresh}).unwrap();
			notifyAnalyzeResult(res, row.domain);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không chấm điểm được đối thủ này')});
		} finally {
			setAnalyzingId(null);
		}
	};

	const onAnalyzeOwn = async (refresh = false) => {
		try {
			const res = await analyzeOwn({projectId, refresh}).unwrap();
			notifyAnalyzeResult(res, own?.domain || 'Website của bạn');
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không chấm điểm được website của dự án')});
		}
	};

	const onDelete = async (row) => {
		try {
			await deleteCompetitor({projectId, id: row.id}).unwrap();
			notification.success({message: 'Đã xoá đối thủ', description: row.domain});
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không xoá được')});
		}
	};

	const onSubmitModal = async (values) => {
		try {
			if (editing) {
				await updateCompetitor({projectId, id: editing.id, name: values.name, note: values.note, url: values.url}).unwrap();
				notification.success({message: 'Đã cập nhật đối thủ', description: editing.domain});
			} else {
				await addCompetitor({projectId, url: values.url, name: values.name, note: values.note}).unwrap();
				notification.success({message: 'Đã thêm đối thủ', description: values.url});
			}
			setModalOpen(false);
			setEditing(null);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không lưu được đối thủ')});
		}
	};

	const scoreCell = (report) => {
		if (!report) return <span className={cn('hint')}>Chưa chấm</span>;
		return (
			<span style={{display: 'inline-flex', alignItems: 'center', gap: 6}}>
				<Tag color={GRADE_COLORS[report.grade] || 'default'}>{report.grade}</Tag>
				<strong>{report.score}</strong>/100
				{report.stale && (
					<Tooltip title="Bản chấm đã quá 24h — bấm Chấm điểm để cập nhật (tốn 1 lượt).">
						<Tag color="orange">cũ</Tag>
					</Tooltip>
				)}
			</span>
		);
	};

	const columns = [
		{
			title: 'Đối thủ', dataIndex: 'domain', key: 'domain', ellipsis: true,
			render: (v, r) => (
				<span>
					<strong>{r.name || v}</strong>{' '}
					<a href={r.url || `https://${v}`} target="_blank" rel="noreferrer" className={cn('hint')}>{v}</a>
					{r.note ? <div className={cn('hint')}>{r.note}</div> : null}
				</span>
			),
		},
		{title: 'Điểm SEO mới nhất', key: 'score', width: 180, render: (_, r) => scoreCell(r.report)},
		{
			// Cột phụ — ẩn dưới 768px cho gọn mobile.
			title: 'Chấm lúc', key: 'at', width: 150, responsive: ['md'],
			render: (_, r) => <span className={cn('hint')}>{r.report?.created || '—'}</span>,
		},
		{
			title: '', key: 'action', align: 'right', width: 170,
			render: (_, r) => (
				<span style={{display: 'inline-flex', gap: 6}}>
					<Popconfirm
						title="Chấm điểm SEO on-page?"
						description="Tốn 1 lượt kiểm tra URL nếu chưa có bản chấm trong 24h."
						okText="Chấm điểm" cancelText="Huỷ"
						onConfirm={() => onAnalyze(r, !!r.report)}
					>
						<Button small primary loading={analyzingId === r.id} disabled={analyzingId !== null && analyzingId !== r.id}
							leftIcon={<FontAwesomeIcon icon="fa-light fa-gauge-high" />}>
							Chấm điểm
						</Button>
					</Popconfirm>
					<Tooltip title="Sửa tên/URL đại diện/ghi chú">
						<Button small white onClick={() => { setEditing(r); setModalOpen(true); }}
							leftIcon={<FontAwesomeIcon icon="fa-light fa-pen" />} />
					</Tooltip>
					<Popconfirm title={`Xoá đối thủ ${r.domain} (kèm lịch sử chấm điểm)?`} okText="Xoá" cancelText="Huỷ"
						onConfirm={() => onDelete(r)}>
						<Button small red leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />} />
					</Popconfirm>
				</span>
			),
		},
	];

	return (
		<div>
			{/* Own site: baseline để so sánh — chấm điểm giống đối thủ. */}
			<div className={cn('ownCard')}>
				<div className={cn('ownMeta')}>
					<FontAwesomeIcon icon="fa-light fa-house-chimney" style={{fontSize: 20, color: '#16a34a'}} />
					<div>
						<strong>Website của bạn</strong>{' '}
						<span className={cn('hint')}>{own?.domain || 'chưa khai báo URL dự án'}</span>
						<div>{scoreCell(own?.report)}</div>
					</div>
				</div>
				<Popconfirm
					title="Chấm điểm website của dự án?"
					description="Tốn 1 lượt kiểm tra URL nếu chưa có bản chấm trong 24h."
					okText="Chấm điểm" cancelText="Huỷ"
					onConfirm={() => onAnalyzeOwn(!!own?.report)}
				>
					<Button small primary loading={analyzingOwn} disabled={!own?.domain}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-gauge-high" />}>
						Chấm điểm site của bạn
					</Button>
				</Popconfirm>
			</div>

			<div className={cn('actionBar')}>
				<div style={{display: 'flex', alignItems: 'center', gap: 10}}>
					<Button primary small onClick={() => { setEditing(null); setModalOpen(true); }}
						disabled={max > 0 && competitors.length >= max}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}>
						Thêm đối thủ
					</Button>
					<span className={cn('hint')}>{competitors.length}{max > 0 ? `/${max}` : ''} đối thủ</span>
				</div>
			</div>

			<Table
				rowKey="id"
				size="small"
				loading={isFetching}
				columns={columns}
				dataSource={competitors}
				locale={{emptyText: 'Chưa có đối thủ nào. Bấm "Thêm đối thủ" để bắt đầu so sánh.'}}
				scroll={{x: 'max-content'}}
				pagination={{pageSize: 10, hideOnSinglePage: true, showSizeChanger: false}}
			/>

			<CompetitorAddModal
				open={modalOpen}
				submitting={adding || updating}
				initial={editing}
				onCancel={() => { setModalOpen(false); setEditing(null); }}
				onSubmit={onSubmitModal}
			/>
		</div>
	);
}

export default CompetitorList;
